import { useMemo, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { IconClose } from "../../../components/Icons";
import { useConfirm } from "../../../components/Dialog";
import { useAuth } from "../../../context/AuthContext";
import { SubscriptionDto } from "../../../types/subscription";

type Props = {
  subscription: SubscriptionDto;
  onClose: () => void;
  onApplied: () => void;
};

const MULTI_TRAITS = ["DEATH", "DISABILITY", "SERIOUS_ILLNESS"] as const;
type MultiTrait = (typeof MULTI_TRAITS)[number];

const fmtMoney = (n: number): string =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);

const SINGLE_MIN = 10000;
const SINGLE_STEP = 5000;
const MULTI_REQUIRED_MIN = 10000;

export default function ReduceCapitalDrawer({
  subscription,
  onClose,
  onApplied,
}: Props) {
  const isMulti = subscription.coverage === "multi";
  if (isMulti) {
    return (
      <MultiCapitalDrawer
        subscription={subscription}
        onClose={onClose}
        onApplied={onApplied}
      />
    );
  }
  return (
    <SingleCapitalDrawer
      subscription={subscription}
      onClose={onClose}
      onApplied={onApplied}
    />
  );
}

function SingleCapitalDrawer({
  subscription,
  onClose,
  onApplied,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [newCapital, setNewCapital] = useState(subscription.capital);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (submitting) return;
    if (newCapital >= subscription.capital) {
      setError("El nuevo capital debe ser menor que el actual");
      return;
    }
    if (newCapital < SINGLE_MIN) {
      setError(`El capital no puede bajar de ${fmtMoney(SINGLE_MIN)}`);
      return;
    }
    const ok = await confirm({
      title: "Reducir capital",
      message: `¿Reducir capital de ${fmtMoney(subscription.capital)} a ${fmtMoney(newCapital)}? Las facturas pendientes se recalcularán.`,
      confirmLabel: "Reducir",
      tone: "warning",
    });
    if (!ok) return;
    setSubmitting(true);
    setError(null);
    try {
      await request(
        `/api/admin/operations/subscriptions/${subscription.id}/reduce-capital`,
        {
          method: "POST",
          token,
          body: { capital: newCapital },
        },
      );
      onApplied();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${((err.body as { message?: string })?.message ?? "").slice(0, 200)}`
          : "Reducir capital falló",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DrawerShell title="Reducir capital" onClose={onClose}>
      <p style={{ margin: "0 0 0.75rem", color: "#73879C", fontSize: ".82rem" }}>
        Capital actual: <strong>{fmtMoney(subscription.capital)}</strong>
      </p>
      <label style={{ display: "grid", gap: "0.3rem" }}>
        <span
          style={{
            fontSize: ".72rem",
            color: "#73879C",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: ".3px",
          }}
        >
          Nuevo capital
        </span>
        <input
          type="number"
          value={newCapital}
          min={SINGLE_MIN}
          max={subscription.capital}
          step={SINGLE_STEP}
          onChange={(e) => setNewCapital(Number(e.target.value))}
          style={{
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            border: "1px solid #dee2e6",
            background: "#f8f9fb",
            fontSize: "1rem",
            fontVariantNumeric: "tabular-nums",
          }}
        />
      </label>
      {error && <ErrorBox>{error}</ErrorBox>}
      <DrawerFooter
        onClose={onClose}
        onSubmit={submit}
        submitting={submitting}
        submitLabel="Reducir capital"
      />
    </DrawerShell>
  );
}

function MultiCapitalDrawer({
  subscription,
  onClose,
  onApplied,
}: Props) {
  const { token } = useAuth();
  const initialCapitals = useMemo(() => {
    const base = subscription.capitalsByTrait ?? {};
    const map: Record<MultiTrait, number> = {
      DEATH: base.DEATH ?? subscription.capital,
      DISABILITY: base.DISABILITY ?? 0,
      SERIOUS_ILLNESS: base.SERIOUS_ILLNESS ?? 0,
    };
    return map;
  }, [subscription.capitalsByTrait, subscription.capital]);
  const [enabled, setEnabled] = useState<Record<MultiTrait, boolean>>({
    DEATH: true,
    DISABILITY: initialCapitals.DISABILITY > 0,
    SERIOUS_ILLNESS: initialCapitals.SERIOUS_ILLNESS > 0,
  });
  const [values, setValues] = useState<Record<MultiTrait, number>>(initialCapitals);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const traitConfigs: Array<{ code: MultiTrait; required: boolean; label: string }> = [
    { code: "DEATH", required: true, label: "Fallecimiento" },
    { code: "DISABILITY", required: false, label: "Invalidez" },
    { code: "SERIOUS_ILLNESS", required: false, label: "Enf. graves" },
  ];

  const submit = async () => {
    if (submitting) return;
    const traitTarifications: Array<{ trait: MultiTrait; capital: number }> = [];
    for (const cfg of traitConfigs) {
      const current = initialCapitals[cfg.code];
      const enabledNow = cfg.required || enabled[cfg.code];
      if (!enabledNow) {
        traitTarifications.push({ trait: cfg.code, capital: 0 });
        continue;
      }
      const value = values[cfg.code];
      if (value > current) {
        setError(`${cfg.label}: el nuevo capital no puede ser mayor que el actual (${fmtMoney(current)})`);
        return;
      }
      if (cfg.required && value < MULTI_REQUIRED_MIN) {
        setError(`${cfg.label}: el capital requerido no puede bajar de ${fmtMoney(MULTI_REQUIRED_MIN)}`);
        return;
      }
      if (value < 0) {
        setError(`${cfg.label}: capital inválido`);
        return;
      }
      traitTarifications.push({ trait: cfg.code, capital: value });
    }
    setError(null);
    setSubmitting(true);
    try {
      const death = traitTarifications.find((t) => t.trait === "DEATH")?.capital ?? 0;
      await request(
        `/api/admin/operations/subscriptions/${subscription.id}/reduce-capital`,
        {
          method: "POST",
          token,
          body: { capital: death, traitTarifications },
        },
      );
      onApplied();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${((err.body as { message?: string })?.message ?? "").slice(0, 200)}`
          : "Reducir capital falló",
      );
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <DrawerShell title="Reducir capital (multi)" onClose={onClose}>
      <p style={{ margin: "0 0 0.75rem", color: "#73879C", fontSize: ".82rem" }}>
        Multi-trait. DEATH es obligatorio. Los demás se pueden desactivar para llevarlos a 0.
      </p>
      <div style={{ display: "grid", gap: "0.7rem" }}>
        {traitConfigs.map((cfg) => {
          const current = initialCapitals[cfg.code];
          const unavailable = !cfg.required && current === 0;
          const isEnabled = cfg.required || enabled[cfg.code];
          return (
            <div
              key={cfg.code}
              style={{
                background: "#f8f9fb",
                border: "1px solid #e9ecef",
                borderRadius: "0.5rem",
                padding: "0.7rem 0.85rem",
                opacity: unavailable ? 0.6 : 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginBottom: "0.45rem",
                }}
              >
                <strong style={{ fontSize: ".82rem", color: "#1f2933" }}>
                  {cfg.label}
                </strong>
                {cfg.required && (
                  <span
                    style={{
                      fontSize: ".68rem",
                      fontWeight: 700,
                      padding: "0.05rem 0.4rem",
                      background: "#0069d9",
                      color: "white",
                      borderRadius: "0.25rem",
                      letterSpacing: ".3px",
                      textTransform: "uppercase",
                    }}
                  >
                    Required
                  </span>
                )}
                {unavailable && (
                  <span
                    style={{
                      fontSize: ".68rem",
                      fontWeight: 700,
                      padding: "0.05rem 0.4rem",
                      background: "#e9ecef",
                      color: "#73879C",
                      borderRadius: "0.25rem",
                      letterSpacing: ".3px",
                      textTransform: "uppercase",
                    }}
                  >
                    No contratado
                  </span>
                )}
                <span style={{ marginLeft: "auto", fontSize: ".75rem", color: "#73879C" }}>
                  Actual: {fmtMoney(current)}
                </span>
                {!cfg.required && (
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.3rem",
                      fontSize: ".75rem",
                      color: "#73879C",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={enabled[cfg.code]}
                      disabled={unavailable}
                      onChange={(e) =>
                        setEnabled((s) => ({ ...s, [cfg.code]: e.target.checked }))
                      }
                    />
                    On
                  </label>
                )}
              </div>
              <input
                type="number"
                value={values[cfg.code]}
                min={cfg.required ? MULTI_REQUIRED_MIN : 0}
                max={current}
                step={SINGLE_STEP}
                onChange={(e) =>
                  setValues((s) => ({ ...s, [cfg.code]: Number(e.target.value) }))
                }
                disabled={!isEnabled || unavailable}
                style={{
                  width: "100%",
                  padding: "0.45rem 0.65rem",
                  borderRadius: "0.4rem",
                  border: "1px solid #dee2e6",
                  background: "white",
                  fontSize: ".95rem",
                  fontVariantNumeric: "tabular-nums",
                }}
              />
            </div>
          );
        })}
      </div>
      {error && <ErrorBox>{error}</ErrorBox>}
      <DrawerFooter
        onClose={onClose}
        onSubmit={() => setConfirmOpen(true)}
        submitting={submitting}
        submitLabel="Revisar cambios"
      />

      {confirmOpen && (
        <ConfirmModal
          onCancel={() => setConfirmOpen(false)}
          onConfirm={submit}
          submitting={submitting}
          rows={traitConfigs.map((cfg) => ({
            label: cfg.label,
            from: initialCapitals[cfg.code],
            to: cfg.required || enabled[cfg.code] ? values[cfg.code] : 0,
          }))}
        />
      )}
    </DrawerShell>
  );
}

function ConfirmModal({
  rows,
  onCancel,
  onConfirm,
  submitting,
}: {
  rows: Array<{ label: string; from: number; to: number }>;
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "0.6rem",
          padding: "1.1rem 1.2rem",
          width: "min(480px, 100%)",
          boxShadow: "0 20px 50px rgba(16, 30, 54, .18)",
        }}
      >
        <h3 style={{ margin: "0 0 0.6rem", fontSize: "1rem", color: "#1f2933" }}>
          Confirma los nuevos capitales
        </h3>
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 0.85rem" }}>
          {rows.map((row) => (
            <li
              key={row.label}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(8rem, auto) 1fr auto 1fr",
                alignItems: "center",
                gap: ".75rem",
                padding: "0.5rem 0.7rem",
                background: "#f6f8fc",
                borderRadius: "0.45rem",
                marginBottom: ".35rem",
                fontSize: ".85rem",
              }}
            >
              <strong style={{ color: "#2A3F54" }}>{row.label}</strong>
              <span
                style={{
                  color: "#73879C",
                  textAlign: "right",
                  textDecoration: "line-through",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtMoney(row.from)}
              </span>
              <span style={{ color: "#0069d9", textAlign: "center" }}>→</span>
              <span
                style={{
                  color: "#0069d9",
                  fontWeight: 700,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtMoney(row.to)}
              </span>
            </li>
          ))}
        </ul>
        <p
          style={{
            fontSize: ".78rem",
            color: "#b32424",
            background: "#fff3cd",
            border: "1px solid #ffe69c",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            margin: 0,
          }}
        >
          Esta operación es <strong>irreversible</strong>. Las facturas pendientes se
          recalcularán inmediatamente.
        </p>
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            justifyContent: "flex-end",
            marginTop: "0.95rem",
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={ghostBtn}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            style={primaryBtn}
          >
            {submitting ? "Aplicando…" : "Confirmar reducción"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DrawerShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 50,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 100vw)",
          height: "100%",
          background: "white",
          boxShadow: "-2px 0 16px rgba(0,0,0,.18)",
          display: "flex",
          flexDirection: "column",
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.95rem 1.1rem",
            borderBottom: "1px solid #e9ecef",
            gap: "0.75rem",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem", color: "#1f2933", flex: 1 }}>
            {title}
          </h2>
          <button type="button" onClick={onClose} style={closeBtn}>
            <IconClose />
          </button>
        </header>
        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.1rem" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function DrawerFooter({
  onClose,
  onSubmit,
  submitting,
  submitLabel,
}: {
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        justifyContent: "flex-end",
        marginTop: "1.1rem",
      }}
    >
      <button type="button" onClick={onClose} style={ghostBtn} disabled={submitting}>
        Cancelar
      </button>
      <button type="button" onClick={onSubmit} style={primaryBtn} disabled={submitting}>
        {submitting ? "Aplicando…" : submitLabel}
      </button>
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: "0.7rem",
        padding: "0.5rem 0.7rem",
        borderRadius: "0.4rem",
        background: "#fde2e2",
        color: "#b32424",
        fontSize: ".8rem",
      }}
    >
      {children}
    </div>
  );
}

const ghostBtn: React.CSSProperties = {
  padding: "0.45rem 0.95rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  padding: "0.45rem 1.1rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const closeBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2rem",
  height: "2rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};
